import { Comparison } from '../../../models/comparison';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType } from '../../../models/enums';
import { NavigationInfo } from '../../../models/navigation-info';
import { Profile } from '../../../models/profile';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { ObjectActionListType, ActionType } from '../../../models/permission';

const comparisonContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({ name: 'Update', iconClass: 'fa-refresh', iconColor: 'color-grey-10' }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        permissions: [{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.DELETE] }]
    })
];

const profileContextMenuOptions: Array<ContextMenuOption> = [
    new ContextMenuOption({
        name: 'Search by Synthetic Document ...', iconClass: 'fa-search', iconColor: 'color-tan-07',
        enabled: (item: GenericTreeNode) => (item.data as Profile).documentHandle !== null
    }),
    new ContextMenuOption({ divider: true }),
    new ContextMenuOption({
        name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01',
        permissions: [{ permissionType: ObjectActionListType.PROFILES, permissionActions: [ActionType.CREATE] }]
    }),
    new ContextMenuOption({
        name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05',
        permissions: [{ permissionType: ObjectActionListType.PROFILES, permissionActions: [ActionType.DELETE] }]
    })
];

export const AnalyticMenu = new GenericTreeNode({
    name: 'Analytics',
    automationId: 'nav-tree-analytics',
    isExpanded: true,
    isExpandable: true,
    isExpandedOnSelect: true,
    isSelectable: false,
    permissions: [{ permissionType: ObjectActionListType.COMPARISONS }, { permissionType: ObjectActionListType.PROFILES }], atLeastOne: true,
    navigationInfo: new NavigationInfo(
        { name: 'All Docs', path: 'document-list', default: true },
        { name: 'Email Threads', path: 'email-thread' },
        { name: 'Short Message Threads', path: 'short-message-thread' },
        { name: 'Communication Grid', path: 'communication-matrix' },
        { name: 'Domain Grid', path: 'domain-matrix' },
        { name: 'Reports', path: 'reports' },
        { name: 'Clusters', path: 'cluster-list' }),
    showContextMenuButton: true,
    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Comparison ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04',
            permissions: [{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'New Synthetic Document ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.PROFILES, permissionActions: [ActionType.CREATE] }]
        }),
    ]
});

export const AnalyticComparisonMenu = new GenericTreeNode({
    name: 'Comparisons',
    entityType: EntityType.comparison,
    automationId: 'nav-tree-analytics-comparisons',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    permissions: [{ permissionType: ObjectActionListType.COMPARISONS }],
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.projectManager_listComparisons,
        responseName: 'comparisons',
        data: {}
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Comparison(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.comparison,
            childPadding: 7.5,
            icon: 'fa-exchange',
            iconColor: 'color-purple-02',
            hasExpanderPadding: false,
            contextMenuOptions: comparisonContextMenuOptions,
            onContextMenuAction: AnalyticMenu.onContextMenuAction,
            isExpandable: false,
            isExpandedOnSelect: false,
            navigationInfo: AnalyticMenu.navigationInfo,
            data: item,
            automationId: 'comparison-' + item.name
        }, handler);
    },

    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Comparison ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.COMPARISONS, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({ name: 'Update' })
    ]
});

export const AnalyticProfileMenu = new GenericTreeNode({
    name: 'Synthetic Documents',
    entityType: EntityType.profile,
    automationId: 'nav-tree-analytics-synthetic-documents',
    isExpandable: true,
    isExpandedOnSelect: true,
    childPadding: 0,
    navigationInfo: new NavigationInfo({ path: 'summary' }),
    permissions: [{ permissionType: ObjectActionListType.PROFILES }],
    requestInfo: new DrRequestInfo({
        endpoint: Endpoint.projectManager_listProfiles,
        responseName: 'profiles',
        data: {}
    }),

    createChild: (data: any, handler?: any): GenericTreeNode => {
        const item = new Profile(data);
        return new GenericTreeNode({
            name: item.name,
            entityType: EntityType.profileHistory,
            childPadding: 7.5,
            icon: 'fa-align-left',
            iconColor: 'color-purple-02',
            contextMenuOptions: profileContextMenuOptions,
            onContextMenuAction: AnalyticMenu.onContextMenuAction,
            isExpandable: false,
            isExpandedOnSelect: false,
            hasExpanderPadding: false,
            navigationInfo: new NavigationInfo(
                { name: 'Synthetic Document', path: 'profile-detail', default: true },
                { name: 'History', path: 'summary' },
            ),
            requestInfo: new DrRequestInfo({
                endpoint: Endpoint.profileManager_listProfileHistories,
                responseName: 'profileHistory',
                data: { handle: item.handle, count: 50, startIndex: 0 }
            }),
            data: item,
            automationId: 'profile-' + item.name
        }, handler);
    },

    contextMenuOptions: [
        new ContextMenuOption({
            name: 'New Synthetic Document ...',
            iconClass: 'fa-plus', iconColor: 'color-green-04', permissions: [{ permissionType: ObjectActionListType.PROFILES, permissionActions: [ActionType.CREATE] }]
        }),
    ]
});

AnalyticMenu.children = [
    AnalyticComparisonMenu,
    AnalyticProfileMenu
];
